import { omit } from 'lodash';

import { Action } from '../actions';
import {
    CLOSE_MASS_ACTIONS_VIDEO_BULK_REPLACEMENT_EDITOR,
    CLOSE_MASS_ACTIONS_VIDEO_CREATIVE_BULK_REPLACEMENT_EDITOR,
    ON_ACCEPT_MASS_ACTIONS_VIDEO_BULK_REPLACEMENT_EDITOR,
    ON_ACCEPT_MASS_ACTIONS_VIDEO_CREATIVE_BULK_REPLACEMENT_EDITOR,
    ON_MASS_ACTIONS_VIDEO_BULK_REPLACEMENT_EDITOR_RADIO_BUTTON_STATE_CHANGED,
    ON_MASS_ACTIONS_VIDEO_CREATIVE_BULK_REPLACEMENT_EDITOR_RADIO_BUTTON_STATE_CHANGED,
    OPEN_MASS_ACTIONS_VIDEO_BULK_REPLACEMENT_EDITOR,
    OPEN_MASS_ACTIONS_VIDEO_CREATIVE_BULK_REPLACEMENT_EDITOR
} from '../actions/banners-edit/banners-edit-mass-actions';
import {
    ADD_NEW_TEXT_BANNERS,
    AddNewTextBannersAction,
    CLEAR_BANNERS,
    COPY_BANNER,
    CopyBanner,
    CREATE_NEW_TEXT_BANNER,
    CreateNewTextBannerAction,
    DELETE_BANNERS,
    RESET_BANNERS_EDIT,
    SAVE_BANNERS_SUCCESS,
    SaveBannersSuccess
} from '../actions/bannersEdit';
import {
    CLOSE_VIDEO_CONSTRUCTOR,
    CloseVideoConstructor,
    OPEN_VIDEO_CONSTRUCTOR,
    OPEN_VIDEO_CONSTRUCTOR_WITH_CPC_VIDEO,
    OpenVideoConstructor,
    OpenVideoConstructorWithCpcVideo,
    REMOVE_BANNER_VIDEO,
    UPDATE_BANNER_CPC_VIDEO_CREATIVE_ID,
    UPDATE_BANNER_VIDEO_CREATIVE_ID,
    UPDATE_BANNER_VIDEO_DATA,
    UPDATE_VIDEOS,
    UpdateBannerCpcVideoCreativeId,
    UpdateBannerVideoCreativeId,
    UpdateBannerVideoData,
    UpdateVideos
} from '../actions/video';
import {
    selectVideoCreativeDataByVideoReplace,
    selectVideoDataByVideoReplace
} from '../selectors/bannerEdit/massActions/massActions';
import {
    BannerId,
    BannersToClear,
    SaveBannersResultData
} from '../typings/banners';
import { EditorPlaceId } from '../typings/common';
import { State } from '../typings/state';
import { VideoState } from '../typings/video';

import { getImageEditorForMassActionsInitialState } from './imageEditor';

const setSavedVideos = (
    state: VideoState,
    action: SaveBannersSuccess
): VideoState => {
    const { added, cpmAdded } = action.result;
    const videoData = state;

    [added, cpmAdded].forEach((addedBanners: SaveBannersResultData) => {
        if (!addedBanners.banners.length) {
            return;
        }

        addedBanners.banners.forEach((addedBanner, index) => {
            if (!addedBanner) {
                return;
            }

            const oldId = addedBanners.ids[index];
            const newId = String(addedBanner.id);

            videoData[newId] = {
                ...videoData[oldId]
            };

            delete videoData[oldId];
        });

        state = videoData;
    });

    return state;
};

export const VideoInitialState: VideoState = {};

export function video(
    state: VideoState = VideoInitialState,
    action: Action,
    fullState: State
): VideoState {
    switch (action.type) {
        case RESET_BANNERS_EDIT:
            return VideoInitialState;
        case COPY_BANNER:
            return copyBannerVideoInfo(state, action);
        case UPDATE_VIDEOS:
            return updateVideos(state, action);
        case CLOSE_VIDEO_CONSTRUCTOR:
            return closeVideoConstructor(state, action);
        case OPEN_VIDEO_CONSTRUCTOR:
            return openVideoConstructor(state, action);
        case OPEN_VIDEO_CONSTRUCTOR_WITH_CPC_VIDEO:
            return openCanvasWithCpcVideo(state, action);
        case UPDATE_BANNER_CPC_VIDEO_CREATIVE_ID:
            return updateBannerVideoCpcCreativeId(state, action);
        case UPDATE_BANNER_VIDEO_CREATIVE_ID:
            return updateBannerVideoCreativeId(state, action);
        case UPDATE_BANNER_VIDEO_DATA:
            return updateBannerVideoData(state, action);
        case REMOVE_BANNER_VIDEO:
            return removeBannersVideo(state, [action.id]);
        case OPEN_MASS_ACTIONS_VIDEO_BULK_REPLACEMENT_EDITOR:
        case CLOSE_MASS_ACTIONS_VIDEO_BULK_REPLACEMENT_EDITOR:
        case ON_MASS_ACTIONS_VIDEO_BULK_REPLACEMENT_EDITOR_RADIO_BUTTON_STATE_CHANGED:
        case OPEN_MASS_ACTIONS_VIDEO_CREATIVE_BULK_REPLACEMENT_EDITOR:
        case CLOSE_MASS_ACTIONS_VIDEO_CREATIVE_BULK_REPLACEMENT_EDITOR:
        case ON_MASS_ACTIONS_VIDEO_CREATIVE_BULK_REPLACEMENT_EDITOR_RADIO_BUTTON_STATE_CHANGED:
            return removeBannersVideo(state, [action.massActionId]);
        case ON_ACCEPT_MASS_ACTIONS_VIDEO_BULK_REPLACEMENT_EDITOR:
            return resetVideos(state, {
                ...selectVideoDataByVideoReplace(
                    fullState,
                    action.massActionId
                ),
                [action.massActionId]: {
                    ...state[action.massActionId],
                    data: undefined
                }
            });
        case ON_ACCEPT_MASS_ACTIONS_VIDEO_CREATIVE_BULK_REPLACEMENT_EDITOR:
            return resetVideos(state, {
                ...selectVideoCreativeDataByVideoReplace(
                    fullState,
                    action.massActionId
                ),
                [action.massActionId]: {
                    ...state[action.massActionId],
                    data: undefined
                }
            });
        case CLEAR_BANNERS:
            return resetBannerVideo(state, action.bannersToClear);
        case DELETE_BANNERS:
            return deleteBannerVideo(state, action.bannersIds, fullState);
        case SAVE_BANNERS_SUCCESS:
            return setSavedVideos(state, action);
        case ADD_NEW_TEXT_BANNERS:
            return addNewVideoToBanners(state, action, fullState);
        case CREATE_NEW_TEXT_BANNER:
            return addNewVideo(state, action);
        default:
            return state;
    }
}

const addNewVideoToBanners = (
    state: VideoState,
    action: AddNewTextBannersAction,
    fullState: State
): VideoState => {
    const newVideos = action.banners.reduce((acc, next, index) => {
        const banner = next;
        const group = action.autoGeneratedVideo[banner.groupId];

        if (group) {
            return {
                ...acc,
                [banner.bannerData.id]: group
            };
        }

        return acc;
    }, {});

    return {
        ...state,
        ...newVideos
    };
};

const addNewVideo = (
    state: VideoState,
    action: CreateNewTextBannerAction
): VideoState => {
    if (!action.video) {
        return state;
    }

    return {
        ...state,
        [action.bannerId]: action.video
    };
};

const resetBannerVideo = (
    state: VideoState,
    bannersToClear: BannersToClear
): VideoState => {
    const result = Object.keys(state).reduce((acc, next) => {
        acc[next] = state[next];

        if (bannersToClear[next]) {
            acc[next] = getImageEditorForMassActionsInitialState();
        }

        return acc;
    }, {});

    return result;
};

const deleteBannerVideo = (
    state: VideoState,
    bannerIds: BannerId[],
    fullState: State
): VideoState => {
    const bannersIdsForDelete = bannerIds.filter(
        id => fullState.editBanners.banners[id].markedForDeletion !== true
    );
    const result = omit(state, bannersIdsForDelete);

    return result;
};

const updateVideos = (state: VideoState, action: UpdateVideos): VideoState => {
    return action.data;
};

const copyBannerVideoInfo = (
    state: VideoState,
    action: CopyBanner
): VideoState => {
    const videoInfo = { ...state[action.bannerId] };

    if (!videoInfo) {
        return state;
    }

    return {
        ...state,
        [action.newBannerId]: videoInfo
    };
};

const resetVideos = (state: VideoState, data: VideoState): VideoState => {
    return {
        ...state,
        ...data
    };
};

const closeVideoConstructor = (
    state: VideoState,
    action: CloseVideoConstructor
): VideoState => {
    return {
        ...state,
        [action.id]: {
            ...state[action.id],
            isCanvasModalOpened: false,
            isCanvasWithCpcVideo: false
        }
    };
};

const openVideoConstructor = (
    state: VideoState,
    action: OpenVideoConstructor
): VideoState => {
    return {
        ...state,
        [action.id]: {
            ...state[action.id],
            isCanvasModalOpened: true
        }
    };
};

const openCanvasWithCpcVideo = (
    state: VideoState,
    action: OpenVideoConstructorWithCpcVideo
): VideoState => {
    return {
        ...state,
        [action.id]: {
            ...state[action.id],
            isCanvasModalOpened: true,
            isCanvasWithCpcVideo: true
        }
    };
};

const updateBannerVideoCpcCreativeId = (
    state: VideoState,
    action: UpdateBannerCpcVideoCreativeId
): VideoState => {
    return {
        ...state,
        [action.id]: {
            ...state[action.id],
            isCanvasModalOpened: false,
            isCanvasWithCpcVideo: false
        }
    };
};

const updateBannerVideoCreativeId = (
    state: VideoState,
    action: UpdateBannerVideoCreativeId
): VideoState => {
    return {
        ...state,
        [action.id]: {
            ...state[action.id],
            isCanvasModalOpened: false
        }
    };
};

const updateBannerVideoData = (
    state: VideoState,
    action: UpdateBannerVideoData
): VideoState => {
    return {
        ...state,
        [action.id]: {
            ...state[action.id],
            data: {
                ...action.data
            }
        }
    };
};

const removeBannersVideo = (
    state: VideoState,
    ids: EditorPlaceId[]
): VideoState => {
    const result = ids.reduce((acc, id) => {
        return {
            [id]: {
                ...state[id],
                data: undefined
            }
        };
    }, {});

    return {
        ...state,
        ...result
    };
};
